import React from 'react'
import { List, useTable } from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { App, Button, Space, Select, Table } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { renderAvatarName } from '../../theme/columns'
import { ListPage, Pill, TableSurface, type Stat } from '../../theme/listkit'
import { supabaseClient } from '../../lib/supabaseClient'
import { groupsAdminErrorMessage } from '../groups/adminErrors'
import { Video, PhoneOff, UsersRound } from '../../components/icons'

type GroupCall = Tables<'group_calls'>
type Group = Tables<'groups'>
type Profile = Tables<'profiles'>

/**
 * Group huddles — every ad-hoc video call a manager has started
 * (`start_huddle`, 20261007120000_group_calls.sql; Groups Phase 5, Task 1),
 * live and ended, across all groups. The scheduled-meetup video path has its
 * own list (Meetups, `group_meetups/list.tsx`) — this page is only the
 * huddle "Start now" side.
 *
 * "End call" runs the SAME `end_huddle` RPC a manager's own Leave/End button
 * calls in the app: one end-a-call entry point, reached from two callers,
 * same split `cancel_meetup` already uses between the organizer's Cancel
 * button and this admin's own Meetups Show page.
 */
export function GroupCallsList() {
  const { message, modal } = App.useApp()
  const { tableProps, filters, setFilters, result: tableResult, tableQuery } = useTable<GroupCall>({
    resource: 'group_calls',
    syncWithLocation: true,
    sorters: { initial: [{ field: 'started_at', order: 'desc' }] },
  })
  const [endingId, setEndingId] = React.useState<string | null>(null)

  const activeLiveFilter = filters.find((f) => 'field' in f && f.field === 'ended_at' && f.operator === 'null')
  const liveOnly = Boolean(activeLiveFilter)

  function setLiveFilter(on: boolean) {
    const rest = filters.filter((f) => !('field' in f) || f.field !== 'ended_at')
    setFilters(on ? [...rest, { field: 'ended_at', operator: 'null', value: true }] : rest, 'replace')
  }

  const groupIds = React.useMemo(
    () => Array.from(new Set(((tableProps.dataSource ?? []) as GroupCall[]).map((c) => c.group_id).filter(Boolean))),
    [tableProps.dataSource],
  )
  const { result: groupResult } = useList<Group>({
    resource: 'groups',
    filters: groupIds.length ? [{ field: 'id', operator: 'in', value: groupIds }] : [],
    pagination: { mode: 'off' },
    queryOptions: { enabled: groupIds.length > 0 },
  })
  const groupMap = React.useMemo<Record<string, string>>(
    () => Object.fromEntries(((groupResult?.data ?? []) as Group[]).map((g) => [g.id, g.name])),
    [groupResult],
  )

  // started_by references auth.users, not profiles — a name lookup needs a
  // second bounded query merged in, same reasoning group_proposals/list.tsx
  // and group_ideas/list.tsx use for created_by/suggested_by.
  const starterIds = React.useMemo(
    () =>
      Array.from(
        new Set(((tableProps.dataSource ?? []) as GroupCall[]).map((c) => c.started_by).filter((id): id is string => Boolean(id))),
      ),
    [tableProps.dataSource],
  )
  const { result: profileResult } = useList<Profile>({
    resource: 'profiles',
    filters: starterIds.length ? [{ field: 'user_id', operator: 'in', value: starterIds }] : [],
    pagination: { mode: 'off' },
    queryOptions: { enabled: starterIds.length > 0 },
  })
  const starterNameMap = React.useMemo<Record<string, string>>(
    () => Object.fromEntries(((profileResult?.data ?? []) as Profile[]).map((p) => [p.user_id, p.name ?? 'Unknown member'])),
    [profileResult],
  )

  const liveQ = useList<GroupCall>({
    resource: 'group_calls',
    filters: [{ field: 'ended_at', operator: 'null', value: true }],
    pagination: { pageSize: 1 },
  })
  const live = liveQ.result?.total ?? 0
  const total = tableResult?.total ?? 0

  const stats: Stat[] = [
    { label: 'Live now', value: live.toLocaleString(), icon: Video, tone: live > 0 ? 'green' : 'neutral' },
    { label: 'All huddles', value: total.toLocaleString(), icon: UsersRound, tone: 'neutral' },
  ]

  async function endCall(record: GroupCall) {
    setEndingId(record.id)
    try {
      const { error } = await supabaseClient.rpc('end_huddle', { p_call: record.id })
      if (error) throw error
      message.success('Call ended')
      tableQuery.refetch()
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    } finally {
      setEndingId(null)
    }
  }

  function confirmEnd(record: GroupCall) {
    modal.confirm({
      title: 'End this call?',
      content: `Everyone currently on ${groupMap[record.group_id] ?? 'this group'}'s huddle will be disconnected.`,
      okText: 'End call',
      okButtonProps: { danger: true },
      onOk: () => endCall(record),
    })
  }

  return (
    <ListPage
      intro={{
        eyebrow: 'Community · Groups',
        description:
          'Ad-hoc video huddles managers have started, live and ended, across all groups. Scheduled meetups with a video/hybrid format live on the Meetups page instead.',
      }}
      stats={stats}
      statColumns={2}
    >
      <List canCreate={false} title={false} breadcrumb={false}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
          <Space size={8}>
            <span style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 12.5, color: brand.sub }}>Show</span>
            <Select
              aria-label="Filter by live status"
              style={{ minWidth: 160 }}
              options={[
                { label: 'All calls', value: 'all' },
                { label: 'Live only', value: 'live' },
              ]}
              value={liveOnly ? 'live' : 'all'}
              onChange={(value: string) => setLiveFilter(value === 'live')}
            />
          </Space>
        </div>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }}>
            <Table.Column<GroupCall>
              dataIndex="group_id"
              title="Group"
              render={(groupId: string) => (
                <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.ink }}>
                  {groupMap[groupId] ?? groupId}
                </span>
              )}
            />
            <Table.Column<GroupCall>
              dataIndex="started_by"
              title="Started by"
              render={(userId: string | null) => renderAvatarName(userId ? starterNameMap[userId] ?? 'Unknown member' : 'Unknown member')}
            />
            <Table.Column<GroupCall>
              dataIndex="started_at"
              title="Started"
              sorter
              render={(value: string) => (
                <span style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13, color: brand.ink }}>
                  {new Date(value).toLocaleString()}
                </span>
              )}
            />
            <Table.Column<GroupCall>
              dataIndex="ended_at"
              title="Status"
              render={(endedAt: string | null) =>
                endedAt ? (
                  <Pill tone="neutral">Ended {new Date(endedAt).toLocaleString()}</Pill>
                ) : (
                  <Space size={4}>
                    <Video size={13} color={brand.sub} />
                    <Pill tone="green">Live</Pill>
                  </Space>
                )
              }
            />
            <Table.Column<GroupCall>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: GroupCall) =>
                record.ended_at ? null : (
                  <Button
                    data-testid={`group-call-end-${record.id}`}
                    danger
                    size="small"
                    icon={<PhoneOff size={13} />}
                    loading={endingId === record.id}
                    onClick={() => confirmEnd(record)}
                  >
                    End call
                  </Button>
                )
              }
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
